<script setup>
import { cloneDeep } from 'lodash-es';
import { inject } from 'vue';
import BIFilter from '~/bi/components/filters/bi-filter-list/bi-filter/bi-filter.vue';
import { useBIColumnPicker } from '~/bi/composables/useBIColumPicker';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { useBiStore } from '~/bi/store/bi.store';

const props = defineProps({
  tables: {
    type: Array,
    required: true,
  },
  stageIndex: {
    type: Number,
  },
  previousStageFields: {
    type: Array,
    default: () => [],
  },
  fields: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['apply']);
const bi_store = useBiStore();
const $services = inject('$services');
const bi_query_builder = useBIQueryBuilder();

const is_expression_dropdown_open = ref(false);
const filter_column_picker_ref = ref(null);

function onApply($event, close = () => {}) {
  emit('apply', $event);
  updateDropdownPosition();
  close?.();
}

const { stages } = useBIColumnPicker(props, 'filters');

function onFilterCancel() {
  filter_column_picker_ref.value?.resetHoveredColumn();
}
onMounted(() => {
});

const dropdown = ref(null);
function updateDropdownPosition() {
  if (dropdown.value) {
    nextTick(() => {
      dropdown.value.setFixedPosition();
    });
  }
}

function getValuesFromExistingFilters(params) {
  const stages = bi_store.widget_builder_config.query.stages.slice(0, props.stageIndex + 1);
  async function getValues(query) {
    const payload = cloneDeep(stages);

    const last_stage = payload[payload.length - 1];

    last_stage.columns = [{
      alias: 'value',
      field: params.columnConfig.field,
      unnest: params.columnConfig.type === 'array',
    }, {
      alias: 'count',
      field: params.columnConfig.field,
      agg: 'count',
    }];

    last_stage.limit = 1000;

    last_stage.filters = last_stage.filters.filter(filter => !filter?.meta?.is_stage_field);

    last_stage.orderBy = [];

    if (query.search) {
      last_stage.filters.push({
        field: params.columnConfig.type === 'array' ? 'value' : params.columnConfig.field,
        op: '__icontains',
        value: query.search,
      });
    }
    last_stage.ignorePreviousStageColumns = true;
    const response = await $services.bi_query.execute({
      id: bi_store.connection_id,
      body: bi_query_builder.getStageConfig(payload),
    });
    const { data } = response.data;
    return data.map(item => item.value);
  }

  return {
    has_filters: stages[stages.length - 1].filters.filter(filter => !filter?.meta?.is_stage_field).length > 0,
    getValues,
  };
}
</script>

<template>
  <hawk-menu ref="dropdown" position="fixed" additional_trigger_classes="!ring-0 !focus:ring-0 !px-0 !py-0" additional_dropdown_classes="transition duration-150 ease-in-out">
    <template #trigger="{ open }">
      <div>
        <slot>
          <Hawk-button
            size="xxs" color="gray" type="light" class="border !border-gray-200 !rounded-md"
            :class="{
              '!bg-gray-700 !text-white': open,
            }"
            icon
          >
            <slot name="trigger_label" />
          </Hawk-button>
        </slot>
      </div>
    </template>
    <template #content="{ close }">
      <div>
        <div>
          <bi-query-builder-column-picker
            ref="filter_column_picker_ref"
            :stages="stages"
            :stage-index="stageIndex"
            :is-filters="true"
            :show-stage-header="false"
            slot-container-classes="!min-h-0 w-[350px]"
            @expression="is_expression_dropdown_open = true"
          >
            <template #default="{ column, table }">
              <BIFilter v-if="column" :key="column" :selected-column-config="{ ...column, table_name: table.name }" :get-values-from-existing-filters="getValuesFromExistingFilters" @cancel="onFilterCancel" @apply="($event) => onApply($event, () => { close() })" />
            </template>
          </bi-query-builder-column-picker>
        </div>
        <bi-filter-expression-editor
          v-if="is_expression_dropdown_open" :tables="tables" :fields="fields" :previous-stage-fields="previousStageFields" @save="($event) => onApply($event, () => { is_expression_dropdown_open = false; close() })" @close="() => { is_expression_dropdown_open = false; close() }"
          @keydown.enter.stop
          @keydown.space.stop
        />
      </div>
    </template>
  </hawk-menu>
</template>
